/**
 * WebSocket Service for Real-time Notifications
 * Connects to backend WebSocket service and handles user-scoped events
 */

import { userService } from './userService';

export interface WebSocketMessage {
  type: string;
  data: any;
  timestamp?: string;
}

export interface TicketData {
  ticket_uuid: string;
  channel: string;
  conversation_id: string;
  creator: {
    name: string;
    contact_value: string;
  };
  title: string;
  label: string;
  status: string;
  created_at: string;
  updated_at: string;
  latest_message: {
    message_id: string;
    sender_email: string;
    subject: string;
    body: string;
    received_at: string;
  };
}

export interface MessageData {
  ticket_uuid: string;
  conversation_id: string;
  message: {
    message_id: string;
    sender: string;
    sender_name?: string;
    subject: string;
    body: string;
    received_at: string;
    channel: string;
    is_echo: boolean;
  };
}

export interface AgentActivityData {
  agent_id: string;
  ticket_uuid: string;
  activity_type: string;
  message?: string;
  timestamp: string;
}

export interface AgentStatusData {
  ticket_uuid: string;
  agent_type: string;
  agent_uuid: string;
  activity_type: 'assigned' | 'started' | 'completed' | 'needs_input' | 'failed' | 'released' | 'progress';
  message?: string;
  progress?: number;
  timestamp: string;
}

export interface DraftData {
  ticket_uuid: string;
  draft_uuid: string;
  draft_body?: string;
  created_by: string;
  created_by_ai: boolean;
  timestamp: string;
}

export interface TicketStatusChangeData {
  ticket_uuid: string;
  new_status: string;
  previous_status?: string;
  updated_at: string;
  updated_by?: string;
}

export interface TicketClassificationData {
  ticket_uuid: string;
  new_label: string;
  previous_label?: string;
  confidence?: number;
  updated_at: string;
}

export interface CreatorUpdateData {
  creator_id: string;
  ticket_uuid?: string;
  creator: {
    id: string;
    name: string;
    email: string;
    type?: string;
    avatar?: string;
    instagram_followers?: number;
    verified?: boolean;
    bio?: string;
    engagement_rate?: number;
    ai_enriched?: boolean;
    enrichment_confidence?: number;
    enrichment_date?: string;
    platforms_discovered?: string[];
    data_quality?: string;
    social_profiles?: Record<string, any>;
    metadata?: Record<string, any>;
  };
  update_type: 'enrichment_completed' | 'profile_updated' | 'data_refreshed';
  timestamp: string;
}

export interface KanbanCardData {
  ticket_uuid: string;
  channel: string;
  conversation_id: string;
  creator: any;
  title?: string;
  label: string;
  status: string;
  project_name?: string;
  project_uuid?: string;
  assigned_team_member?: string;
  created_at: string;
  updated_at: string;
  last_message_preview?: string;
  mapped_creator_id?: string;
}

export interface NewMessageData {
  conversation_id: string;
  message_data: {
    message_id: string;
    sender: string;
    username?: string;
    profile_image_url?: string;
    text?: string;
    content?: string;
    subject?: string;
    received_at: string;
    is_echo?: boolean;
    platform: 'outlook' | 'instagram';
    to_recipients?: string[];
    cc_recipients?: string[];
    bcc_recipients?: string[];
  };
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private apiKey: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private listeners: Map<string, Array<(data: any) => void>> = new Map();
  private connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error' = 'disconnected';
  private statusListeners: Array<(status: string) => void> = [];
  private reconnectTimeout: NodeJS.Timeout | null = null;

  // WebSocket endpoint - auto-select by environment
  private readonly WS_ENDPOINT = (() => {
    const fromEnv = process.env.REACT_APP_WS_URL;
    if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
    if (typeof window !== 'undefined') {
      const host = window.location.host || '';
      const hostname = window.location.hostname || '';
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
      const isDevSite = host.includes('saas-application-dev.s3-website.eu-north-1.amazonaws.com');
      if (isLocalhost || isDevSite) return 'wss://hqztok64m8.execute-api.eu-north-1.amazonaws.com/development';
    }
    return 'wss://hqztok64m8.execute-api.eu-north-1.amazonaws.com/production';
  })();

  /**
   * Initialize WebSocket connection
   */
  async connect(apiKey?: string): Promise<void> {
    this.apiKey = apiKey || null;

    // Prevent multiple simultaneous connections
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    if (this.ws?.readyState === WebSocket.CONNECTING || this.connectionStatus === 'connecting') {
      console.log('WebSocket connection already in progress');
      return;
    }

    this.setConnectionStatus('connecting');
    
    // Build connection URL
    let wsUrl = this.WS_ENDPOINT;
    const params = new URLSearchParams();
    
    try {
      // Try to get Cognito auth token first (modern approach)
      const authToken = await userService.getAuthToken();
      if (authToken) {
        // For WebSocket API Gateway with Cognito, pass token as query parameter
        params.append('access_token', authToken);
        console.log('🔌 Using Cognito authentication');
      } else if (apiKey) {
        params.append('api_key', apiKey);
        console.log('🔌 Using API key authentication');
      } else {
        console.log('🔌 No authentication provided');
      }
    } catch (error) {
      // Fallback to API key if Cognito auth fails
      if (apiKey) {
        params.append('api_key', apiKey);
        console.log('🔌 Cognito auth failed, using API key');
      } else {
        console.log('🔌 No authentication available');
      }
    }
    
    if (params.toString()) {
      wsUrl += `?${params.toString()}`;
    }

    console.log('🔌 Connecting to WebSocket:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.setConnectionStatus('error');
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    console.log('🔌 Disconnecting WebSocket');
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
    
    // Clear any pending reconnection attempts
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.setConnectionStatus('disconnected');
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('✅ WebSocket connected successfully');
      this.setConnectionStatus('connected');
      this.reconnectAttempts = 0;
      
      // Send a ping to test the connection
      this.sendMessage({
        type: 'ping',
        data: { timestamp: new Date().toISOString() }
      });
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        console.log('📨 WebSocket message received:', message);
        this.handleMessage(message);
      } catch (error) {
        console.error('❌ Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onclose = (event) => {
      console.log('🔌 WebSocket connection closed:', event.code, event.reason);
      this.setConnectionStatus('disconnected');
      
      // Attempt to reconnect if not intentionally closed
      if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      this.setConnectionStatus('error');
    };
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: WebSocketMessage): void {
    const { type, data } = message;

    switch (type) {
      case 'connection_established':
        console.log('🎉 Connection established:', data);
        break;

      case 'new_ticket':
        console.log('🎫 New ticket received:', data);
        this.emit('new_ticket', data as TicketData);
        break;

      case 'new_message_in_ticket':
        console.log('💬 New message in ticket:', data);
        // Emit the raw data - let the components handle the format
        this.emit('new_message_in_ticket', data);
        break;

      case 'ticket_updated':
        console.log('📝 Ticket updated:', data);
        this.emit('ticket_updated', data);
        break;

      case 'ticket_status_changed':
        console.log('🔄 Ticket status changed:', data);
        this.emit('ticket_status_changed', data as TicketStatusChangeData);
        break;

      case 'ticket_classified':
        console.log('🏷️ Ticket classified:', data);
        this.emit('ticket_classified', data as TicketClassificationData);
        break;

      case 'ticket_deleted':
        console.log('🗑️ Ticket deleted:', data);
        this.emit('ticket_deleted', data);
        break;

      case 'agent_activity':
        console.log('🤖 Agent activity:', data);
        this.emit('agent_activity', data as AgentActivityData);
        break;

      case 'agent.activity':
        console.log('🤖 Agent streaming activity:', data);
        this.emit('agent.activity', data);
        break;

      case 'agent_status_update':
        console.log('🔧 Agent status update:', data);
        this.emit('agent_status_update', data as AgentStatusData);
        break;

      case 'draft_created':
        console.log('📝 Draft created:', data);
        this.emit('draft_created', data as DraftData);
        break;

      case 'draft_updated':
        console.log('✏️ Draft updated:', data);
        this.emit('draft_updated', data as DraftData);
        break;

      case 'draft_deleted':
        console.log('🗑️ Draft deleted:', data);
        this.emit('draft_deleted', data as DraftData);
        break;

      case 'message_status_updated':
        console.log('🔄 Message status updated:', data);
        this.emit('message_status_updated', data);
        break;

      case 'message_sent':
        console.log('📤 Message sent:', data);
        this.emit('message_sent', data);
        break;

      case 'message_classified':
        console.log('🏷️ Message classified:', data);
        this.emit('message_classified', data);
        break;

      case 'creator_updated':
        console.log('👤 Creator updated:', data);
        this.emit('creator_updated', data as CreatorUpdateData);
        break;

      case 'agent_update':
        console.log('🤖 Copilot agent update:', data);
        this.emit('agent_update', data);
        break;

      case 'copilot_progress':
        console.log('🚀 Copilot progress update:', data);
        this.emit('copilot_progress', data);
        break;

      case 'kanban_card_created':
        console.log('🎫 Kanban card created:', data);
        this.emit('kanban_card_created', data as KanbanCardData);
        break;

      case 'kanban_card_updated':
        console.log('📝 Kanban card updated:', data);
        this.emit('kanban_card_updated', data as KanbanCardData);
        break;

      case 'new_message':
        console.log('💬 New message received:', data);
        this.emit('new_message', data as NewMessageData);
        break;

      case 'message_content_updated':
        console.log('✏️ Message content updated:', data);
        this.emit('message_content_updated', data);
        break;

      case 'pong':
        console.log('🏓 Pong received');
        break;

      case 'echo':
        console.log('📡 Echo received:', data);
        break;

      default:
        console.log('❓ Unknown message type:', type, data);
        this.emit('unknown_message', { type, data });
    }
  }

  /**
   * Send message through WebSocket
   */
  sendMessage(message: WebSocketMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      console.log('📤 Sent WebSocket message:', message);
    } else {
      console.warn('⚠️ WebSocket not connected, cannot send message:', message);
    }
  }

  /**
   * Add event listener
   */
  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  /**
   * Remove event listener
   */
  off(event: string, callback: (data: any) => void): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const index = eventListeners.indexOf(callback);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to listeners
   */
  private emit(event: string, data: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ Error in WebSocket event listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('❌ Max reconnection attempts reached');
      return;
    }

    // Clear any existing reconnection timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff
    
    console.log(`🔄 Scheduling reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    
    this.reconnectTimeout = setTimeout(async () => {
      this.reconnectTimeout = null;
      if (this.connectionStatus !== 'connected') {
        await this.connect(this.apiKey || undefined);
      }
    }, delay);
  }

  /**
   * Set connection status and notify listeners
   */
  private setConnectionStatus(status: typeof this.connectionStatus): void {
    this.connectionStatus = status;
    this.statusListeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('❌ Error in status listener:', error);
      }
    });
  }

  /**
   * Add connection status listener
   */
  onStatusChange(callback: (status: string) => void): void {
    this.statusListeners.push(callback);
  }

  /**
   * Remove connection status listener
   */
  offStatusChange(callback: (status: string) => void): void {
    const index = this.statusListeners.indexOf(callback);
    if (index > -1) {
      this.statusListeners.splice(index, 1);
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get current connection status
   */
  getConnectionStatus(): string {
    return this.connectionStatus;
  }

}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService; 